import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import PageHeader from '@/shared/components/PageHeader';
import {
  Button, Card, Drawer, Input, InputNumber, Select, Space, Table, Tag, Typography,
} from '@/shared/ui/primitives';
import { CheckOutlined, CloseOutlined, EditOutlined, EyeOutlined, ReloadOutlined, StarFilled } from '@/shared/ui/icons';
import { PUBLIC_WEB_URL } from '@/config/env';
import { blogApi, type BlogPost, type BlogPostInput, type BlogStatus } from '../api/blog.api';
import type { ColumnsType } from '@/shared/ui/subpath-types';

const STATUS_COLORS: Record<BlogStatus, string> = {
  DRAFT: 'warning',
  PUBLISHED: 'success',
  REJECTED: 'error',
  ARCHIVED: 'default',
};

const emptyDraft: BlogPostInput = {
  locale: 'ar',
  title: '',
  slug: '',
  category: '',
  excerpt: '',
  content: '',
  image: '/images/blog/find-roommate-rent-saudi-arabia.jpg',
  metaTitle: '',
  metaDescription: '',
  keywords: '',
  status: 'DRAFT',
};

export default function BlogPostsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<{ locale: string; status: BlogStatus | ''; search: string }>({ locale: 'ar', status: '', search: '' });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editing, setEditing] = useState<BlogPostInput | null>(null);
  const [generate, setGenerate] = useState({ locale: 'ar', count: 10, topicHint: '' });

  const listQuery = useQuery({
    queryKey: ['blog-posts', filters, page],
    queryFn: () => blogApi.list({ ...filters, page, size: 20 }),
  });

  const detailQuery = useQuery({
    queryKey: ['blog-post', selectedId],
    queryFn: () => blogApi.get(selectedId!),
    enabled: selectedId != null,
  });

  useEffect(() => {
    if (detailQuery.data) setEditing(detailQuery.data);
  }, [detailQuery.data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['blog-posts'] });
    if (selectedId) qc.invalidateQueries({ queryKey: ['blog-post', selectedId] });
  };

  const saveMutation = useMutation({
    mutationFn: (body: BlogPostInput) => selectedId ? blogApi.update(selectedId, body) : blogApi.create(body),
    onSuccess: (post) => {
      toast.success('تم حفظ المقال');
      setSelectedId(post.id);
      setEditing(post);
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'تعذر حفظ المقال'),
  });

  const publishMutation = useMutation({
    mutationFn: blogApi.publish,
    onSuccess: (post) => { toast.success('تم نشر المقال'); setEditing(post); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.message || 'تعذر نشر المقال'),
  });

  const rejectMutation = useMutation({
    mutationFn: blogApi.reject,
    onSuccess: (post) => { toast.success('تم رفض المقال'); setEditing(post); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.message || 'تعذر رفض المقال'),
  });

  const generateMutation = useMutation({
    mutationFn: blogApi.generateDrafts,
    onSuccess: (posts) => {
      toast.success(posts.length ? `تم إنشاء ${posts.length} مسودات` : 'لم يتم إنشاء مسودات، تحقق من إعداد OpenAI');
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'تعذر توليد المقالات'),
  });

  const records = listQuery.data?.content || [];

  const columns: ColumnsType<BlogPost> = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: 'المقال',
      width: 360,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 700 }}>{r.title}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{r.slug}</div>
        </div>
      ),
    },
    { title: 'اللغة', dataIndex: 'locale', width: 80, render: (v) => <Tag>{v}</Tag> },
    { title: 'التصنيف', dataIndex: 'category', width: 140, render: (v) => v || '—' },
    { title: 'الحالة', dataIndex: 'status', width: 130, render: (v: BlogStatus) => <Tag color={STATUS_COLORS[v]}>{v}</Tag> },
    { title: 'AI', dataIndex: 'generatedByAi', width: 80, render: (v) => v ? <Tag color="purple">AI</Tag> : <Tag>حالي</Tag> },
    { title: 'تاريخ النشر', dataIndex: 'publishDate', width: 130, render: (v) => v || '—' },
    {
      title: 'إجراءات',
      width: 160,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => { setSelectedId(r.id); setEditing(r); }} />
          {r.status !== 'PUBLISHED' && <Button size="small" icon={<CheckOutlined />} onClick={() => publishMutation.mutate(r.id)} />}
          <Button size="small" icon={<EyeOutlined />} onClick={() => window.open(`${PUBLIC_WEB_URL}/${r.locale}/blog/${r.slug}`, '_blank', 'noopener,noreferrer')} />
        </Space>
      ),
    },
  ];

  const updateEditing = (key: keyof BlogPostInput, value: any) => {
    setEditing((current) => ({ ...(current || emptyDraft), [key]: value }));
  };

  const canSave = useMemo(() => !!editing?.locale && !!editing?.title && !!editing?.content, [editing]);

  return (
    <div>
      <PageHeader
        title="إدارة المقالات"
        subtitle="المقالات الحالية مستوردة هنا، ومقالات الذكاء الاصطناعي تبقى مسودات حتى تنشرها."
        action={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => listQuery.refetch()} />
            <Button type="primary" icon={<EditOutlined />} onClick={() => { setSelectedId(null); setEditing(emptyDraft); }}>مقال جديد</Button>
          </Space>
        )}
      />

      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 160px minmax(180px, 1fr) auto', gap: 8 }}>
          <Select value={filters.locale} onChange={(v: any) => { setPage(0); setFilters((f) => ({ ...f, locale: v })); }} options={[{ value: 'ar', label: 'عربي' }, { value: 'en', label: 'English' }]} />
          <Select allowClear placeholder="الحالة" value={filters.status || undefined} onChange={(v: any) => { setPage(0); setFilters((f) => ({ ...f, status: v || '' })); }} options={['DRAFT', 'PUBLISHED', 'REJECTED', 'ARCHIVED'].map((v) => ({ value: v, label: v }))} />
          <Input.Search placeholder="بحث في العنوان أو الرابط" value={filters.search} onChange={(e: any) => setFilters((f) => ({ ...f, search: e.target.value }))} />
          <Button onClick={() => setFilters({ locale: 'ar', status: '', search: '' })}>تصفير</Button>
        </div>
      </Card>

      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 140px minmax(180px, 1fr) auto', gap: 8, alignItems: 'center' }}>
          <Select value={generate.locale} onChange={(v: any) => setGenerate((g) => ({ ...g, locale: v }))} options={[{ value: 'ar', label: 'عربي' }, { value: 'en', label: 'English' }]} />
          <InputNumber min={1} max={10} value={generate.count} onChange={(v: any) => setGenerate((g) => ({ ...g, count: Number(v || 10) }))} />
          <Input placeholder="توجيه اختياري للمواضيع" value={generate.topicHint} onChange={(e: any) => setGenerate((g) => ({ ...g, topicHint: e.target.value }))} />
          <Button type="primary" icon={<StarFilled />} loading={generateMutation.isPending} onClick={() => generateMutation.mutate(generate)}>توليد مسودات</Button>
        </div>
      </Card>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={records}
        loading={listQuery.isLoading}
        scroll={{ x: 1100 }}
        pagination={{
          current: page + 1,
          pageSize: 20,
          total: listQuery.data?.totalElements || 0,
          onChange: (p) => setPage(p - 1),
          showSizeChanger: false,
        }}
      />

      <Drawer
        open={!!editing}
        width={820}
        title={selectedId ? `تحرير مقال #${selectedId}` : 'مقال جديد'}
        onClose={() => { setEditing(null); setSelectedId(null); }}
        footer={(
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Space>
              {selectedId && <Button icon={<CheckOutlined />} loading={publishMutation.isPending} onClick={() => publishMutation.mutate(selectedId)}>نشر</Button>}
              {selectedId && <Button danger icon={<CloseOutlined />} loading={rejectMutation.isPending} onClick={() => rejectMutation.mutate(selectedId)}>رفض</Button>}
            </Space>
            <Button type="primary" disabled={!canSave} loading={saveMutation.isPending} onClick={() => editing && saveMutation.mutate(editing)}>حفظ</Button>
          </Space>
        )}
      >
        {editing && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 160px', gap: 8 }}>
              <Select value={editing.locale} onChange={(v: any) => updateEditing('locale', v)} options={[{ value: 'ar', label: 'عربي' }, { value: 'en', label: 'English' }]} />
              <Input placeholder="العنوان" value={editing.title} onChange={(e: any) => updateEditing('title', e.target.value)} />
              <Select value={editing.status} onChange={(v: any) => updateEditing('status', v)} options={['DRAFT', 'PUBLISHED', 'REJECTED', 'ARCHIVED'].map((v) => ({ value: v, label: v }))} />
            </div>
            <Input placeholder="slug" value={editing.slug || ''} onChange={(e: any) => updateEditing('slug', e.target.value)} />
            <Input placeholder="التصنيف" value={editing.category || ''} onChange={(e: any) => updateEditing('category', e.target.value)} />
            <Input placeholder="الصورة" value={editing.image || ''} onChange={(e: any) => updateEditing('image', e.target.value)} />
            <Input.TextArea rows={3} placeholder="المقتطف" value={editing.excerpt || ''} onChange={(e: any) => updateEditing('excerpt', e.target.value)} />
            <Input.TextArea rows={14} placeholder="HTML content" value={editing.content || ''} onChange={(e: any) => updateEditing('content', e.target.value)} />
            <Input placeholder="Meta title" value={editing.metaTitle || ''} onChange={(e: any) => updateEditing('metaTitle', e.target.value)} />
            <Input.TextArea rows={3} placeholder="Meta description" value={editing.metaDescription || ''} onChange={(e: any) => updateEditing('metaDescription', e.target.value)} />
            <Input.TextArea rows={2} placeholder="Keywords" value={editing.keywords || ''} onChange={(e: any) => updateEditing('keywords', e.target.value)} />
            <Typography.Text type="secondary">المحتوى يقبل HTML مثل المقالات الحالية. المقال لا يظهر في الموقع إلا إذا كانت حالته PUBLISHED.</Typography.Text>
          </div>
        )}
      </Drawer>
    </div>
  );
}

